import {
  Loading,
  useWrappedConnection,
  useInfo,
} from "~lib/ConnectionProvider";
import { ALL_APPS, AppDefn, isInGameState } from "~lib/App";
import * as React from "react";
import { LobbyConnection } from "./LobbyConnection";
import { AppName, GameName } from "~shared/messageTypes";
import { LobbyClient, LobbyServer } from "~shared/namespace/Lobby";
import { usePlayerInThisGame } from "~lib/App";
import { DataFromType } from "~shared/Connection";
import { ConnectionState } from "~/App";
import {
  AlertTriangle,
  Dices,
  Rocket,
  Target,
  CheckCircle2,
} from "lucide-react";

export const LobbyApp: AppDefn<{
  inGame: boolean;
  connection?: LobbyConnection;
  info?: DataFromType<LobbyServer.Type["LOBBY_INFO"]>;
}> = {
  hooks(active, { connectionState }) {
    const inGame = usePlayerInThisGame(AppName.LOBBY);
    const connection = useWrappedConnection(inGame, LobbyConnection);
    const info = useInfo(
      connectionState === ConnectionState.READY && active,
      LobbyServer.TypeValue.LOBBY_INFO
    );
    return { inGame, connection, info };
  },
  app({ inGame, connection, info, roomInfo }) {
    const playersInGame = (roomInfo?.players ?? []).filter((x) =>
      isInGameState(x.joinState)
    ).length;
    return !info ? (
      <Loading />
    ) : (
      <div className="space-y-4">
        {!inGame && (
          <div className="bg-yellow-500/20 border-2 border-yellow-500/50 rounded-xl p-3 backdrop-blur-sm">
            <p className="text-yellow-200 font-semibold flex items-center gap-2 text-sm">
              <AlertTriangle size={20} />
              Join the game to select and start games!
            </p>
          </div>
        )}

        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border-2 border-white/20">
          <h2 className="mb-2 flex items-center gap-2">
            <Dices className="text-purple-300" size={20} />
            Current Game
          </h2>
          <div className="text-xl font-bold text-yellow-300">
            {ALL_APPS.find((x) => x.id === info.game)?.name ||
              "No game selected"}
          </div>

          {info.game !== null && inGame && (
            <div className="mt-3 space-y-2">
              <button
                className="w-full"
                disabled={!inGame || !connection || playersInGame < 2}
                onClick={() => {
                  if (!connection) return;
                  connection.sendMessageWithType(
                    LobbyClient.TypeValue.START_GAME,
                    void 0
                  );
                }}
              >
                <Rocket className="inline-block mr-2" size={18} />
                Start Game
              </button>
              {playersInGame < 2 && (
                <p className="text-center text-yellow-300 font-semibold text-sm">
                  Need at least 2 players to start
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <h2 className="mb-3 flex items-center gap-2">
            <Target className="text-green-300" size={20} />
            Available Games
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ALL_APPS.filter((x) => !x.hidden).map((x, i) => (
              <button
                key={x.id.toString()}
                disabled={!inGame || !connection}
                className={`p-3 text-left transition-all ${
                  info.game === x.id ? "ring-4 ring-yellow-400 scale-105" : ""
                }`}
                onClick={() => {
                  if (!connection) return;
                  connection.sendMessageWithType(
                    LobbyClient.TypeValue.SET_GAME,
                    x.id as GameName
                  );
                }}
              >
                {x.id === info.game ? (
                  <CheckCircle2
                    className="inline-block mr-2 text-yellow-300"
                    size={20}
                  />
                ) : (
                  <Target className="inline-block mr-2" size={20} />
                )}
                {x.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  },
};
